'use client'

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode
} from 'react'
// Removed: import { io, Socket } from 'socket.io-client'
// Removed: import { ToolExecutor } from './ToolExecutor'
import { AIAction, AITool, ComponentReference } from './types'
import { globalAIState } from './globalAIState'
import { SidebarProvider } from "@/components/ui/sidebar"
import { AISidebar } from './AISidebar'

interface AITerminalProps {
  roomId: string
  userId: string
  userName: string
  children: ReactNode
}

interface AITerminalContextProps {
  // Component registration for AI control
  registerComponent: (id: string, component: ComponentReference) => void
  unregisterComponent: (id: string) => void
  
  // Action execution (now handled by API routes)
  executeAction: (action: AIAction) => Promise<any>
  
  // Chat interface
  isAIChatOpen: boolean
  toggleAIChat: () => void
  
  // Tool registration (now handled directly by API routes)
  registerTool: (tool: AITool) => void
  getAvailableTools: () => AITool[]
  
  // Context sharing for AI
  getPageContext: () => any
  updatePageContext: (context: any) => void
}

const AITerminalContext = createContext<AITerminalContextProps | null>(null)

export function useAITerminal() {
  const context = useContext(AITerminalContext)
  if (!context) {
    throw new Error('useAITerminal must be used within an AITerminalProvider')
  }
  return context
}

export function useOptionalAITerminal() {
  return useContext(AITerminalContext)
}

export default function AITerminal({
  roomId,
  userId,
  userName,
  children
}: AITerminalProps) {
  // AI Chat state - now synced with global state
  const [isAIChatOpen, setIsAIChatOpen] = useState(globalAIState.getState().isAIChatOpen)
  
  // Component registry for AI control (no sockets needed)
  const [components, setComponents] = useState<Map<string, ComponentReference>>(new Map())
  
  // Tool registry (informational only - actual tools are in API route)
  const [tools, setTools] = useState<Map<string, AITool>>(new Map())
  
  // Page context for AI understanding
  const [pageContext, setPageContext] = useState<any>({})

  // Register with global state manager
  useEffect(() => {
    globalAIState.setAITerminalActive(true)
    
    // Subscribe to global state changes
    const unsubscribe = globalAIState.subscribe((state) => {
      setIsAIChatOpen(state.isAIChatOpen)
    })
    
    return () => {
      globalAIState.setAITerminalActive(false)
      unsubscribe()
    }
  }, [])

  // Component registration (pure React - no sockets)
  const registerComponent = (id: string, component: ComponentReference) => {
    setComponents(prev => new Map(prev).set(id, component))
    console.log(`🔧 Component registered: ${id} with ${component.capabilities.length} capabilities`)
  }

  const unregisterComponent = (id: string) => {
    setComponents(prev => {
      const newMap = new Map(prev)
      newMap.delete(id)
      return newMap
    })
    console.log(`🗑️ Component unregistered: ${id}`)
  }

  // Tool registration (informational - actual execution in API route)
  const registerTool = (tool: AITool) => {
    setTools(prev => {
      if (prev.has(tool.name)) {
        return prev
      }
      const newTools = new Map(prev).set(tool.name, tool)
      console.log(`🔧 Tool registered: ${tool.name}`)
      return newTools
    })
  }

  const getAvailableTools = () => Array.from(tools.values())

  // Action execution (simplified - components handle their own events now)
  const executeAction = async (action: AIAction): Promise<any> => {
    console.log('🎬 Executing action:', action)
    
    // For socketless implementation, we just dispatch events directly
    if (action.type === 'tool_call') {
      const event = new CustomEvent(action.toolName || 'genericAction', {
        detail: action.parameters
      })
      window.dispatchEvent(event)
      return { success: true, message: 'Action dispatched via CustomEvent' }
    }
    
    return { success: true, message: 'Action executed' }
  }

  // Chat interface controls
  const toggleAIChat = () => {
    globalAIState.toggleAIChat()
  }

  // Page context management
  const getPageContext = () => pageContext

  const updatePageContext = (context: any) => {
    setPageContext((prev: any) => ({ ...prev, ...context }))
    console.log('📋 Page context updated:', { ...pageContext, ...context })
  }

  const contextValue: AITerminalContextProps = {
    registerComponent,
    unregisterComponent,
    executeAction,
    isAIChatOpen,
    toggleAIChat,
    registerTool,
    getAvailableTools,
    getPageContext,
    updatePageContext
  }

  return (
    <AITerminalContext.Provider value={contextValue}>
      <SidebarProvider>
        <div className="flex h-screen w-full overflow-hidden">
          <main className="flex-1 overflow-auto min-w-0">
            {children}
          </main>
          
          {/* AI Sidebar - Pure React implementation */}
          {isAIChatOpen && (
            <AISidebar
              socket={null} // No socket needed
              roomId={roomId}
              userId={userId}
              tools={getAvailableTools()}
              context={{
                ...pageContext,
                availableTools: getAvailableTools(),
                components: Array.from(components.values()),
                roomId,
                userId,
                userName,
                // Additional context for AI
                componentCount: components.size,
                toolCount: tools.size
              }}
              onClose={() => globalAIState.setAIChatOpen(false)}
            />
          )}
        </div>
      </SidebarProvider>
    </AITerminalContext.Provider>
  )
} 